"use client";

import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import { TASK_STATUS_LABELS, TASK_STATUS_SELECTABLE_ORDER, type TaskStatus } from "@quovy/types";

import { PropertyTrigger } from "./property-trigger";
import { SelectedCheck } from "./selected-check";
import { TaskStatusLabel } from "./task-status-label";

/** Re-exported for back-compat callers; prefer `TaskStatus` from `@quovy/types`. */
export type TaskUiStatus = TaskStatus;

const SELECTABLE_STATUSES: readonly TaskStatus[] = TASK_STATUS_SELECTABLE_ORDER;

export interface StatusSelectorProps {
  /** Current status */
  value: TaskStatus;
  /** Callback when status changes */
  onSelect: (status: TaskStatus) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function StatusSelector({ value, onSelect, disabled = false }: StatusSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PropertyTrigger role="combobox" aria-expanded={open} disabled={disabled}>
          <TaskStatusLabel status={value} />
        </PropertyTrigger>
      </PopoverTrigger>
      <PopoverContent className="w-[180px] p-0" align="start">
        <Command>
          <CommandInput
            ref={inputRef}
            placeholder="Search…"
            className="focus:outline-none focus:ring-0"
          />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
              {SELECTABLE_STATUSES.map((status) => (
                <CommandItem
                  key={status}
                  value={status}
                  keywords={[TASK_STATUS_LABELS[status]]}
                  onSelect={() => {
                    onSelect(status);
                    setOpen(false);
                  }}
                  className="py-1.5"
                >
                  <TaskStatusLabel status={status} />
                  {value === status && <SelectedCheck />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
